import { useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { getPortalIntakeForm, submitPortalIntakeForm } from '../../services/portalService';
import Spinner from '../../components/common/Spinner';

function QuestionField({ question, value, onChange }) {
  const { type, label, required, options = [], id } = question;

  const inputClass = 'input mt-1';

  if (type === 'text') {
    return (
      <div>
        <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
          {label}{required && <span className="text-danger-500 ml-1">*</span>}
        </label>
        <input
          type="text"
          className={inputClass}
          value={value || ''}
          onChange={(e) => onChange(id, e.target.value)}
        />
      </div>
    );
  }

  if (type === 'textarea') {
    return (
      <div>
        <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
          {label}{required && <span className="text-danger-500 ml-1">*</span>}
        </label>
        <textarea
          className={inputClass}
          rows={4}
          value={value || ''}
          onChange={(e) => onChange(id, e.target.value)}
        />
      </div>
    );
  }

  if (type === 'date') {
    return (
      <div>
        <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
          {label}{required && <span className="text-danger-500 ml-1">*</span>}
        </label>
        <input
          type="date"
          className={inputClass}
          value={value || ''}
          onChange={(e) => onChange(id, e.target.value)}
        />
      </div>
    );
  }

  if (type === 'radio') {
    return (
      <div>
        <p className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
          {label}{required && <span className="text-danger-500 ml-1">*</span>}
        </p>
        <div className="mt-2 space-y-2">
          {options.map((opt) => (
            <label key={opt} className="flex items-center gap-2 text-sm text-neutral-700 dark:text-neutral-300">
              <input
                type="radio"
                name={id}
                value={opt}
                checked={value === opt}
                onChange={() => onChange(id, opt)}
              />
              {opt}
            </label>
          ))}
        </div>
      </div>
    );
  }

  if (type === 'checkbox') {
    // Single boolean checkbox (consent / agreement style — no options list)
    if (options.length === 0) {
      return (
        <label className="flex items-start gap-2 text-sm text-neutral-700 dark:text-neutral-300 cursor-pointer">
          <input
            type="checkbox"
            className="mt-0.5 shrink-0"
            checked={value === true}
            onChange={(e) => onChange(id, e.target.checked)}
          />
          <span>{label}{required && <span className="text-danger-500 ml-1">*</span>}</span>
        </label>
      );
    }

    // Multi-option checkbox
    const selected = Array.isArray(value) ? value : [];
    const toggle = (opt) => {
      const next = selected.includes(opt)
        ? selected.filter((x) => x !== opt)
        : [...selected, opt];
      onChange(id, next);
    };
    return (
      <div>
        <p className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
          {label}{required && <span className="text-danger-500 ml-1">*</span>}
        </p>
        <div className="mt-2 space-y-2">
          {options.map((opt) => (
            <label key={opt} className="flex items-center gap-2 text-sm text-neutral-700 dark:text-neutral-300">
              <input
                type="checkbox"
                checked={selected.includes(opt)}
                onChange={() => toggle(opt)}
              />
              {opt}
            </label>
          ))}
        </div>
      </div>
    );
  }

  if (type === 'select') {
    return (
      <div>
        <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
          {label}{required && <span className="text-danger-500 ml-1">*</span>}
        </label>
        <select
          className={inputClass}
          value={value || ''}
          onChange={(e) => onChange(id, e.target.value)}
        >
          <option value="">— Select —</option>
          {options.map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      </div>
    );
  }

  return null;
}

function SignaturePad({ onCapture, onClear }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);

  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const src = e.touches ? e.touches[0] : e;
    return {
      x: (src.clientX - rect.left) * (canvas.width / rect.width),
      y: (src.clientY - rect.top) * (canvas.height / rect.height),
    };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getPos(e, canvas);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  };

  const draw = (e) => {
    e.preventDefault();
    if (!drawing.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getPos(e, canvas);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = '#111827';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.stroke();
  };

  const stop = () => {
    drawing.current = false;
    const dataUrl = canvasRef.current.toDataURL('image/png');
    onCapture(dataUrl);
  };

  const clear = () => {
    const canvas = canvasRef.current;
    canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
    onClear();
  };

  return (
    <div>
      <canvas
        ref={canvasRef}
        width={500}
        height={160}
        className="border border-neutral-300 rounded-lg w-full touch-none cursor-crosshair"
        onMouseDown={start}
        onMouseMove={draw}
        onMouseUp={stop}
        onMouseLeave={stop}
        onTouchStart={start}
        onTouchMove={draw}
        onTouchEnd={stop}
      />
      <button type="button" onClick={clear} className="mt-2 text-sm text-neutral-500 hover:text-neutral-900 dark:hover:text-neutral-100 dark:text-neutral-300">
        Clear signature
      </button>
    </div>
  );
}

export default function PortalIntakeFormFill() {
  const { responseId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [step, setStep] = useState(1);
  const [answers, setAnswers] = useState({});
  const [signatureData, setSignatureData] = useState(null);
  const [validationError, setValidationError] = useState('');

  const { data, isLoading, isError } = useQuery({
    queryKey: ['portal', 'intakeForm', responseId],
    queryFn: () => getPortalIntakeForm(responseId),
  });

  const mutation = useMutation({
    mutationFn: () => submitPortalIntakeForm(responseId, { answers, signatureData }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portal', 'intakeForms'] });
      toast.success('Form submitted successfully');
      navigate('/portal');
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to submit form'),
  });

  const setAnswer = (id, value) => {
    setAnswers((prev) => ({ ...prev, [id]: value }));
  };

  const handleNextStep = () => {
    const form = data?.form;
    const missing = (form.schema || [])
      .filter((q) => q.required)
      .filter((q) => {
        const val = answers[q.id];
        if (val === undefined || val === null || val === '') return true;
        if (val === false) return true;
        if (Array.isArray(val) && val.length === 0) return true;
        return false;
      });

    if (missing.length > 0) {
      setValidationError(`Please answer all required questions: ${missing.map((q) => q.label).join(', ')}`);
      return;
    }
    setValidationError('');
    setStep(2);
  };

  if (isLoading) return <div className="flex justify-center py-20"><Spinner /></div>;
  if (isError) return <div className="text-center py-20 text-danger-600">Failed to load form.</div>;

  const response = data;
  const form = response.form;
  const totalSteps = 2;
  const progress = Math.round((step / totalSteps) * 100);

  if (response.submittedAt) {
    return (
      <div className="card text-center py-12 max-w-md mx-auto">
        <p className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-2">Already submitted</p>
        <p className="text-neutral-500 dark:text-neutral-400 text-sm">This form has already been completed.</p>
        <button onClick={() => navigate('/portal')} className="btn btn-primary mt-6">Back to home</button>
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto">
      {/* Sticky progress bar */}
      <div className="sticky top-0 z-10 -mx-4 sm:-mx-6 px-4 sm:px-6 pt-2 pb-3 bg-neutral-50 dark:bg-neutral-950 border-b border-neutral-200 dark:border-neutral-800 mb-6">
        <div className="h-1.5 bg-neutral-200 dark:bg-neutral-700 rounded-full overflow-hidden">
          <div
            className="h-full bg-teal-600 rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
        <p className="text-xs text-neutral-500 mt-1">Step {step} of {totalSteps}</p>
      </div>

      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-neutral-900 dark:text-neutral-100">{form.title}</h1>
        {form.description && <p className="text-neutral-500 mt-1 text-sm">{form.description}</p>}
      </div>

      {step === 1 && (
        <div className="card space-y-6">
          {(form.schema || []).map((question) => (
            <QuestionField
              key={question.id}
              question={question}
              value={answers[question.id]}
              onChange={setAnswer}
            />
          ))}

          {validationError && (
            <p className="text-sm text-danger-600 dark:text-danger-400" role="alert">{validationError}</p>
          )}

          <div className="flex justify-end pt-2">
            <button onClick={handleNextStep} className="btn btn-primary">
              Next: Sign &amp; submit
            </button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="card space-y-6">
          <div>
            <h2 className="text-base font-semibold text-neutral-900 dark:text-neutral-100 mb-2">Your signature</h2>
            <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-4">
              Draw your signature below to confirm and submit this form.
            </p>
            <SignaturePad
              onCapture={setSignatureData}
              onClear={() => setSignatureData(null)}
            />
          </div>

          <p className="text-xs text-neutral-500 dark:text-neutral-400">
            By clicking &ldquo;I agree and submit&rdquo;, you acknowledge that the information provided is accurate
            and you consent to its use as part of your care record.
          </p>

          <div className="flex justify-between pt-2">
            <button onClick={() => setStep(1)} className="btn btn-secondary">Back</button>
            <button
              onClick={() => mutation.mutate()}
              disabled={mutation.isPending}
              className="btn btn-primary disabled:opacity-60"
            >
              {mutation.isPending ? 'Submitting…' : 'I agree and submit'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
